import {
  DASHBOARD_CATEGORIES,
  DASHBOARD_STATUSES,
  HISTORICAL_DATA_REGION_IDS,
  REQUIRED_PRODUCTS,
  type AdminDashboard,
  type AdminDashboardInput,
  type DashboardCategory,
  type DashboardKind,
  type DashboardStatus,
  type RequiredProduct,
} from "@/api/dataProducts.service";
import { DEFAULT_TABLEAU_HOST } from "./dashboardKinds";

export type DashboardFormValues = {
  title: string;
  slug: string;
  description: string;
  category: DashboardCategory;
  requiredProduct: RequiredProduct;
  regionId: string;
  edition: string;
  status: DashboardStatus;
  sortOrder: string;
  omniContentId: string;
  omniLinkAccess: string;
  omniCustomThemeId: string;
  tableauHost: string;
  tableauViewPath: string;
};

export type DashboardFormErrors = Partial<Record<keyof DashboardFormValues, string>>;

export const SLUG_PATTERN = /^[a-z0-9-]+$/;

export function emptyFormValues(kind: DashboardKind): DashboardFormValues {
  return {
    title: "",
    slug: "",
    description: "",
    category: kind === "tableau" ? "historical_data" : "submissions_analysis",
    requiredProduct: kind === "tableau" ? "historical_data" : "sars",
    regionId: "",
    edition: "",
    status: "draft",
    sortOrder: "0",
    omniContentId: "",
    omniLinkAccess: "",
    omniCustomThemeId: "",
    tableauHost: kind === "tableau" ? DEFAULT_TABLEAU_HOST : "",
    tableauViewPath: "",
  };
}

export function toFormValues(dashboard: AdminDashboard): DashboardFormValues {
  return {
    title: dashboard.title,
    slug: dashboard.slug,
    description: dashboard.description ?? "",
    category: dashboard.category,
    requiredProduct: dashboard.required_product,
    regionId: dashboard.region_id ?? "",
    edition: dashboard.edition ?? "",
    status: dashboard.status,
    sortOrder: String(dashboard.sort_order),
    omniContentId: dashboard.omni_content_id ?? "",
    omniLinkAccess: dashboard.omni_link_access.join(", "),
    omniCustomThemeId: dashboard.omni_custom_theme_id ?? "",
    tableauHost: dashboard.tableau_host ?? "",
    tableauViewPath: dashboard.tableau_view_path ?? "",
  };
}

function nullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

export function parseLinkAccess(value: string): string[] {
  return value
    .split(",")
    .map(entry => entry.trim())
    .filter(entry => entry !== "");
}

// The other kind's fields go out as null / [] rather than omitted, so a row never keeps stale
// embed settings.
export function toDashboardInput(
  kind: DashboardKind,
  values: DashboardFormValues,
): AdminDashboardInput {
  const isOmni = kind === "omni";
  const isTableau = kind === "tableau";
  return {
    slug: values.slug.trim(),
    title: values.title.trim(),
    description: nullable(values.description),
    kind,
    category: values.category,
    required_product: values.requiredProduct,
    region_id: isTableau ? nullable(values.regionId) : null,
    edition: isTableau ? nullable(values.edition) : null,
    status: values.status,
    sort_order: Number.parseInt(values.sortOrder, 10),
    omni_content_id: isOmni ? nullable(values.omniContentId) : null,
    omni_link_access: isOmni ? parseLinkAccess(values.omniLinkAccess) : [],
    omni_custom_theme_id: isOmni ? nullable(values.omniCustomThemeId) : null,
    tableau_host: isTableau ? nullable(values.tableauHost) : null,
    tableau_view_path: isTableau ? nullable(values.tableauViewPath) : null,
  };
}

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

export function validateDashboardForm(
  kind: DashboardKind,
  values: DashboardFormValues,
): DashboardFormErrors {
  const errors: DashboardFormErrors = {};

  if (values.title.trim() === "") {
    errors.title = "Title is required";
  }

  const slug = values.slug.trim();
  if (slug === "") {
    errors.slug = "Slug is required";
  } else if (!SLUG_PATTERN.test(slug)) {
    errors.slug = "Slug may contain only lowercase letters, digits and hyphens";
  }

  if (!DASHBOARD_CATEGORIES.includes(values.category)) {
    errors.category = "Category is required";
  }
  if (!REQUIRED_PRODUCTS.includes(values.requiredProduct)) {
    errors.requiredProduct = "Required product is required";
  }
  if (!DASHBOARD_STATUSES.includes(values.status)) {
    errors.status = "Status is required";
  }

  const sortOrder = values.sortOrder.trim();
  if (!/^-?\d+$/.test(sortOrder)) {
    errors.sortOrder = "Sort order must be a whole number";
  }

  if (kind === "tableau") {
    const host = values.tableauHost.trim();
    if (host === "") {
      errors.tableauHost = "Tableau host is required";
    } else if (!isHttpsUrl(host)) {
      errors.tableauHost = "Tableau host must be an https URL";
    }
    if (values.tableauViewPath.trim() === "") {
      errors.tableauViewPath = "Tableau view path is required";
    }
    if (values.category === "historical_data") {
      const region = values.regionId.trim();
      if (region === "") {
        errors.regionId = "Region is required for a Historical Data dashboard";
      } else if (!(HISTORICAL_DATA_REGION_IDS as readonly string[]).includes(region)) {
        errors.regionId = `Region must be one of ${HISTORICAL_DATA_REGION_IDS.join(", ")}`;
      }
    }
  }

  return errors;
}

const API_FIELD_TO_FORM_KEY: Record<string, keyof DashboardFormValues> = {
  title: "title",
  slug: "slug",
  description: "description",
  category: "category",
  required_product: "requiredProduct",
  region_id: "regionId",
  edition: "edition",
  status: "status",
  sort_order: "sortOrder",
  omni_content_id: "omniContentId",
  omni_link_access: "omniLinkAccess",
  omni_custom_theme_id: "omniCustomThemeId",
  tableau_host: "tableauHost",
  tableau_view_path: "tableauViewPath",
};

type ApiError = Error & {
  status?: number;
  code?: string;
  details?: Array<{ code: string; path: (string | number)[]; message: string }>;
  detailsRaw?: unknown;
};

export function describeSaveError(error: unknown): {
  fields: DashboardFormErrors;
  message: string | null;
} {
  const apiError = error as ApiError;
  const fields: DashboardFormErrors = {};
  const unplaced: string[] = [];

  if (apiError?.status === 409) {
    return { fields: { slug: apiError.message }, message: null };
  }

  if (apiError?.status === 400 && Array.isArray(apiError.details)) {
    for (const detail of apiError.details) {
      const key = API_FIELD_TO_FORM_KEY[String(detail.path[0] ?? "")];
      if (key && !fields[key]) {
        fields[key] = detail.message;
      } else {
        unplaced.push(detail.message);
      }
    }
  }

  const placedAny = Object.keys(fields).length > 0;
  if (unplaced.length > 0) {
    return { fields, message: unplaced.join(" ") };
  }
  if (placedAny) {
    return { fields, message: null };
  }
  return {
    fields,
    message:
      error instanceof Error && error.message ? error.message : "The dashboard could not be saved",
  };
}

export type PreviewFailure = { message: string; issues: string[] };

const NOT_CONFIGURED_MESSAGE =
  "This dashboard can't be previewed until its configuration is fixed.";

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(entry => typeof entry === "string");
}

export function describePreviewError(error: unknown, knownIssues: string[]): PreviewFailure {
  const apiError = error as ApiError;
  if (apiError?.status === 503 && apiError.code === "dashboard_not_configured") {
    const issues =
      knownIssues.length > 0
        ? knownIssues
        : isStringArray(apiError.detailsRaw)
          ? apiError.detailsRaw
          : [];
    return { message: NOT_CONFIGURED_MESSAGE, issues };
  }
  return {
    message:
      error instanceof Error && error.message
        ? error.message
        : "The preview could not be generated",
    issues: [],
  };
}
